import React from "react";
import { Link } from "react-router-dom";
import { useInView } from "../hooks/useInView";
import "./Products.css";

function Reveal({ children, className = "", delay = 0 }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`reveal ${inView ? "revealed" : ""} ${className}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
}

const PRODUCTS = [
  {
    name: "Cold-Pressed Sesame Oil",
    tag: "Primary Export Product",
    gold: true,
    /* ── SESAME IMAGE ──────────────────────────────────────────────────────
       Change this path to use a different sesame oil image.
       File must be in: frontend/public/images/
       Example: '/images/sesame-new.jpg'
    ─────────────────────────────────────────────────────────────────────── */
    img: "/images/sesame.jpeg",
    skus: ["250 ml", "500 ml (Hero SKU)", "1 L", "Bulk on request"],
    specs: [
      ["Extraction", "Cold-pressed, low-temperature"],
      ["Refinement", "Unrefined"],
      ["Additives", "None / Clean label"],
      ["Shelf life", "As per batch documentation"],
      ["Markets", "UK · EU · Middle East"],
    ],
    points: [
      "Balanced fatty acid profile (oleic + linoleic)",
      "Natural sesame aroma retained through cold process",
      "Suitable for foodservice, retail & specialty channels",
      "Consistent batch specs available on request",
    ],
  },
  {
    name: "Cold-Pressed Coconut Oil",
    tag: "Available on volume",
    gold: false,
    /* ── COCONUT IMAGE ────────────────────────────────────────────────────
       Change /images/cocunut.jpeg to your coconut oil image.
       File must be in: frontend/public/images/
       (Note: original filename is cocunut.jpeg — rename if needed)
    ─────────────────────────────────────────────────────────────────────── */
    img: "/images/cocunut.jpeg",
    skus: ["500 ml", "1 L", "5 L"],
    specs: [
      ["Extraction", "Cold-pressed"],
      ["Refinement", "Unrefined / Virgin"],
      ["Lauric Acid", "Naturally high"],
      ["Additives", "None"],
    ],
    points: [
      "Sourced from Tiptur copra, Karnataka",
      "Natural coconut aroma retained",
      "Virgin variant available",
    ],
  },

  {
    name: "Cold-Pressed Groundnut Oil",
    tag: "Available on volume",
    gold: false,
    /* ── GROUNDNUT IMAGE ──────────────────────────────────────────────────
       Change /images/nut.jpeg to your groundnut oil image.
       File must be in: frontend/public/images/
    ─────────────────────────────────────────────────────────────────────── */
    img: "/images/nut.jpeg",
    skus: ["500 ml", "1 L", "5 L", "Bulk on request"],
    specs: [
      ["Extraction", "Cold-pressed"],
      ["Refinement", "Unrefined"],
      ["Smoke Point", "High — suitable for frying"],
      ["Additives", "None"],
    ],
    points: [
      "Mild, neutral flavour — versatile for cooking",
      "High smoke point for frying applications",
      "Popular in South Asian and HoReCa markets",
    ],
  },

  {
    name: "Cold-Pressed Castor Oil",
    tag: "Personal care / specialty",
    gold: false,
    /* ── CASTOR IMAGE ─────────────────────────────────────────────────────
       Change /images/castor.jpeg to your castor oil image.
       File must be in: frontend/public/images/
    ─────────────────────────────────────────────────────────────────────── */
    img: "/images/castor.jpeg",
    skus: ["250 ml", "500 ml", "1 L"],
    specs: [
      ["Extraction", "Cold-pressed"],
      ["Grade", "Personal care / cosmetic"],
      ["Ricinoleic acid", "Naturally high"],
      ["Additives", "None"],
    ],
    points: [
      "Hair care and scalp applications",
      "Deep moisturisation for skin",
      "Chemical-free, no hexane",
    ],
  },
];

export default function Products() {
  return (
    <main className="products-page">
      <section className="products-page__hero">
        <div className="products-page__hero-inner">
          <Reveal>
            <span className="eyebrow eyebrow--light">Our Range</span>
            <h1 className="products-page__h1">Cold-Pressed Oils</h1>
            <p className="products-page__sub">
              Specifications, SKUs, and availability for export and distribution
              partners.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="products-page__list">
        {PRODUCTS.map((p, i) => (
          <div
            key={p.name}
            className={`prod-row ${i % 2 === 1 ? "prod-row--flip" : ""}`}
          >
            <Reveal dir="l" className="prod-row__img-col">
              <div
                className={`prod-row__img-wrap ${
                  p.gold ? "prod-row__img-wrap--gold" : ""
                }`}
              >
                <img src={p.img} alt={p.name} />
                {p.gold && (
                  <div className="prod-row__primary-badge">
                    Primary Export Product
                  </div>
                )}
              </div>
            </Reveal>
            <Reveal dir="r" className="prod-row__content">
              <div className="prod-row__tag">{p.tag}</div>
              <h2 className="h2">{p.name}</h2>
              <div className="prod-row__skus">
                {p.skus.map((s) => (
                  <span
                    key={s}
                    className={`sku-pill ${
                      s.includes("Hero") ? "sku-pill--hero" : ""
                    }`}
                  >
                    {s}
                  </span>
                ))}
              </div>
              <table className="prod-row__specs">
                <tbody>
                  {p.specs.map(([k, v]) => (
                    <tr key={k}>
                      <td>{k}</td>
                      <td>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <ul className="prod-row__points">
                {p.points.map((pt) => (
                  <li key={pt}>{pt}</li>
                ))}
              </ul>
              <Link
                to="/contact"
                className="btn btn--outline"
                style={{ marginTop: "1.5rem", display: "inline-flex" }}
              >
                Request Info →
              </Link>
            </Reveal>
          </div>
        ))}
      </section>

      <section className="products-page__cta">
        <Reveal>
          <h2>Need Bulk Specifications or Samples?</h2>
          <p>
            Contact us for technical datasheets, pricing, and sample requests.
          </p>
          <Link to="/contact" className="btn btn--gold">
            Contact Us →
          </Link>
        </Reveal>
      </section>

      <footer className="footer">
        <div className="footer__inner">
          <div className="footer__brand">
            <img
              src="/images/logo.jpeg"
              alt="Nativeroots"
              className="footer__logo"
            />
            <div>
              <div className="footer__name">Nativeroots</div>
              <div className="footer__sub">
                S R Industries, Tumkur, Karnataka
              </div>
            </div>
          </div>
          <nav className="footer__nav">
            <Link to="/">Home</Link>
            <Link to="/products">Products</Link>
            <Link to="/contact">Contact</Link>
          </nav>
          <div className="footer__contact">
            <a href="tel:+918861432099">+91 88614 32099</a>
            <div>
              <a href="mailto:rakeshcsmail@gmail.com">rakeshcsmail@gmail.com</a>
              <br />
              <a href="mailto:rakesh@nativerootsheritage.co.in">rakesh@nativerootsheritage.co.in</a>
            </div>
          </div>
        </div>
        <div className="footer__bar">
          © {new Date().getFullYear()} Nativeroots · S R Industries
        </div>
      </footer>
    </main>
  );
}
